// searchUtils.js — drop next to ItemDatabaseManager.jsx

function normalise(str) {
  if (!str) return "";
  return String(str)
    .toLowerCase()
    .replace(/[''"""]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function buildSearchBlob(item) {
  const parts = [
    item.itemCode,
    item.description,
    item.sectionNumber || item.costCode?.code || "",
    item.uom,
    item.size,
    item.manufacturer,
    item.partNumber,
    item.itemNotes,
    item.defaultVendor?.companyName || "",
    ...(item.vendorPrices || []).map((vp) => vp.vendor?.companyName || ""),
    ...(item.vendorPrices || []).map((vp) => vp.partNumber || ""),
  ];
  return normalise(parts.join("  "));
}

function tokenVariants(token) {
  const variants = [token];

  const fractionCondensed = token.match(/^(\d+)(\d)(\d)$/);
  if (fractionCondensed) {
    const [, a, b, c] = fractionCondensed;
    variants.push(`${a}-${b}/${c}`);
    variants.push(`${a} ${b}/${c}`);
    variants.push(`${a}${b}/${c}`);
  }

  const fractionExpanded = token.match(/^(\d+)[\s-]?(\d)\/(\d)$/);
  if (fractionExpanded) {
    const [, a, b, c] = fractionExpanded;
    variants.push(`${a}${b}${c}`);
  }

  const gaugeMatch = token.match(/^(\d+)(ga|gauge)$/i);
  if (gaugeMatch) {
    const [, num] = gaugeMatch;
    variants.push(`${num}ga`);
    variants.push(`${num} ga`);
    variants.push(`${num}gauge`);
  }

  return variants;
}

function scoreItem(item, tokens) {
  let score = 0;
  const fullQuery = tokens.join(" ");

  const code = normalise(item.itemCode);
  const desc = normalise(item.description);

  // Exact item code match — strongest signal
  if (code === fullQuery) {
    score += 10000;
  } else if (code.startsWith(fullQuery)) {
    score += 5000;
  } else if (code.includes(fullQuery)) {
    score += 2000;
  }

  // Exact description match
  if (desc === fullQuery) {
    score += 8000;
  } else if (desc.startsWith(fullQuery)) {
    score += 3000;
  }

  // Per-token scoring across weighted fields
  const fields = [
    { text: code, weight: 50 },
    { text: desc, weight: 30 },
    {
      text: normalise(item.sectionNumber || item.costCode?.code || ""),
      weight: 10,
    },
    { text: normalise(item.manufacturer), weight: 10 },
    { text: normalise(item.partNumber), weight: 10 },
    { text: normalise(item.size), weight: 8 },
  ];

  for (const token of tokens) {
    const variants = tokenVariants(token);
    for (const { text, weight } of fields) {
      if (!text) continue;
      for (const v of variants) {
        if (text === v) {
          score += weight * 3; // exact field match
          break;
        } else if (text.startsWith(v)) {
          score += weight * 2; // starts with
          break;
        } else if (text.includes(v)) {
          score += weight; // contains
          break;
        }
      }
    }
  }

  // Bonus: shorter descriptions rank higher for same match quality (more specific item)
  score -= desc.length * 0.01;

  return score;
}

export function multiTokenSearch(items, searchStr) {
  const raw = normalise(searchStr);
  if (!raw) return items;

  const tokens = raw.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return items;

  // Filter first
  const matched = items.filter((item) => {
    const blob = item._searchBlob || buildSearchBlob(item);
    if (!item._searchBlob) item._searchBlob = blob;
    return tokens.every((token) => {
      const variants = tokenVariants(token);
      return variants.some((v) => blob.includes(v));
    });
  });

  // Score and sort — best matches first
  return matched
    .map((item) => ({ item, score: scoreItem(item, tokens) }))
    .sort((a, b) => b.score - a.score)
    .map((entry) => entry.item);
}

export function clearSearchCache(items) {
  items.forEach((item) => {
    delete item._searchBlob;
  });
}
